import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Archive, ChevronDown, ChevronRight, Copy, Save, GitCompare,
  Download, RotateCcw, Ban, Check, X, Layers,
} from "lucide-react";
import { api } from "@/lib/api";

const FIELDS = [
  { key: "mods", prefix: "Mods=", label: "Mods=", prod: "mods", flaggable: true },
  { key: "workshop_items", prefix: "WorkshopItems=", label: "WorkshopItems=", prod: "workshop" },
  { key: "map", prefix: "Map=", label: "Map=", prod: "map" },
];

export const parseLine = (raw) => {
  const body = (raw || "").trim().replace(/^[A-Za-z]+=/, "");
  const seen = new Set();
  const out = [];
  for (const part of body.split(/[;\n]/)) {
    const v = part.trim();
    if (!v || seen.has(v.toLowerCase())) continue;
    seen.add(v.toLowerCase());
    out.push(v);
  }
  return out;
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function ManualBackup({ canEdit, production, copyText }) {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({ mods: "", workshop_items: "", map: "" });
  const [open, setOpen] = useState(true);
  const [showDiff, setShowDiff] = useState(false);
  const [busy, setBusy] = useState(false);

  const apply = (d) => {
    setData(d);
    setDrafts({ mods: d.mods, workshop_items: d.workshop_items, map: d.map });
  };

  useEffect(() => {
    api.get("/mod-tracker/backup").then((r) => apply(r.data)).catch(() => toast.error("Couldn't load backup modlist."));
  }, []);

  const dirty = data && FIELDS.some((f) => drafts[f.key] !== data[f.key]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const flags = useMemo(() => {
    const m = new Map();
    for (const f of data?.not_approved || []) m.set(f.mod_id.toLowerCase(), f);
    return m;
  }, [data]);

  const frameworks = useMemo(
    () => new Set((data?.frameworks || []).map((id) => id.toLowerCase())),
    [data]
  );

  const run = async (fn, okMsg, onData = apply) => {
    setBusy(true);
    try {
      const r = await fn();
      onData(r.data);
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(() => api.put("/admin/mod-tracker/backup", drafts), "Backup modlist saved.");
  const setFlag = (mod_id, not_approved, reason = "") =>
    run(() => api.put("/admin/mod-tracker/backup/not-approved", { mod_id, not_approved, reason }),
      not_approved ? `${mod_id} marked not approved.` : `${mod_id} un-flagged.`,
      // Only refresh flags — never overwrite unsaved text in the boxes.
      (d) => setData((prev) => ({ ...prev, not_approved: d.not_approved })));

  const setFramework = (mod_id, framework) =>
    run(() => api.put("/admin/mod-tracker/backup/framework", { mod_id, framework }),
      framework ? `${mod_id} tagged as framework.` : `${mod_id} framework tag removed.`,
      (d) => setData((prev) => ({ ...prev, frameworks: d.frameworks })));

  const copyFromProduction = () => {
    const hasText = FIELDS.some((f) => (drafts[f.key] || "").trim());
    if (hasText && !window.confirm("Replace the backup boxes with the current Production lines? (Nothing is saved until you click Save.)")) return;
    setDrafts({ mods: production.mods, workshop_items: production.workshop, map: production.map });
    toast.success("Production lines copied into the backup. Review, then Save.");
  };

  if (!data) return null;

  return (
    <div className="mt-6 border border-[#B9763A]/45 bg-[#1F150C]/40 p-4 space-y-4" data-testid="mod-backup">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="mod-backup-toggle"
        className="w-full flex items-center gap-2 text-left group"
      >
        {open ? <ChevronDown size={14} className="text-[#D9A066]" /> : <ChevronRight size={14} className="text-[#D9A066]" />}
        <Archive size={14} className="text-[#D9A066]" />
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-[#D9A066] group-hover:text-[#F0C08E]">
          // Manual backup modlist. Never auto-edited.
        </span>
        <span className="ml-auto font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#8F7357]" data-testid="mod-backup-meta">
          {data.updated_at ? `Saved ${fmt(data.updated_at)} · ${data.updated_by}` : "Never saved"}
          {flags.size > 0 && ` · ${flags.size} not approved`}
        </span>
      </button>

      {open && (
        <>
          <div className="flex flex-wrap gap-2">
            {canEdit && (
              <BarBtn onClick={save} disabled={!dirty || busy} testid="mod-backup-save" solid>
                <Save size={12} /> {dirty ? "Save backup" : "Saved"}
              </BarBtn>
            )}
            {canEdit && (
              <BarBtn onClick={copyFromProduction} disabled={busy} testid="mod-backup-copy-from-prod">
                <Download size={12} /> Copy from Production
              </BarBtn>
            )}
            <BarBtn onClick={() => setShowDiff((v) => !v)} active={showDiff} testid="mod-backup-diff-toggle">
              <GitCompare size={12} /> Diff vs Production
            </BarBtn>
            {dirty && (
              <BarBtn onClick={() => apply(data)} disabled={busy} testid="mod-backup-discard">
                <X size={12} /> Discard edits
              </BarBtn>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4">
            {FIELDS.map((f) => (
              <BackupField
                key={f.key}
                field={f}
                value={drafts[f.key]}
                readOnly={!canEdit}
                onChange={(v) => setDrafts((d) => ({ ...d, [f.key]: v }))}
                onCopy={() => copyText(drafts[f.key], `Backup ${f.label}`)}
              />
            ))}
          </div>

          {showDiff && (
            <DiffPanel
              drafts={drafts} production={production} flags={flags} frameworks={frameworks}
              canEdit={canEdit} busy={busy} onFlag={setFlag} onFramework={setFramework}
            />
          )}
          <NotApprovedList flags={flags} canEdit={canEdit} busy={busy} onFlag={setFlag} />
        </>
      )}
    </div>
  );
}

function BarBtn({ children, onClick, disabled, testid, solid, active }) {
  const base = "inline-flex items-center gap-1.5 px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
  const look = solid
    ? "bg-[#D9A066] hover:bg-[#c48b52] text-[#0D110C]"
    : active
      ? "border border-[#D9A066] bg-[#D9A066]/15 text-[#F0C08E]"
      : "border border-[#5A4430] hover:border-[#D9A066] text-[#D9A066]";
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} className={`${base} ${look}`}>
      {children}
    </button>
  );
}

function BackupField({ field, value, readOnly, onChange, onCopy }) {
  const count = parseLine(value).length;
  return (
    <div className="bg-[#151B14] border border-[#3A2E22] p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-[#8D9A87]">
          // Backup {field.label} <span className="text-[#D9A066]" data-testid={`mod-backup-${field.key}-count`}>({count})</span>
        </span>
        <button
          type="button"
          onClick={onCopy}
          disabled={!value.trim()}
          data-testid={`mod-backup-${field.key}-copy`}
          className="inline-flex items-center gap-1.5 border border-[#D9A066] text-[#D9A066] hover:bg-[#D9A066]/15 disabled:opacity-40 px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.25em] transition-colors"
        >
          <Copy size={11} /> Copy
        </button>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        rows={field.key === "mods" ? 6 : 3}
        spellCheck={false}
        placeholder={`${field.prefix}ModA;ModB;ModC`}
        data-testid={`mod-backup-${field.key}`}
        className="mt-3 w-full bg-[#0D110C] border border-[#283524] text-[#F0C08E] px-3 py-2 font-mono-ui text-[11px] break-all focus:border-[#D9A066] focus:outline-none resize-y"
      />
    </div>
  );
}

function DiffPanel({ drafts, production, flags, frameworks, canEdit, busy, onFlag, onFramework }) {
  const [showShared, setShowShared] = useState(false);
  const modExtra = (id) => (
    <>
      <FrameworkToggle id={id} on={frameworks.has(id.toLowerCase())} canEdit={canEdit} busy={busy} onToggle={onFramework} />
      <FlagControl id={id} flag={flags.get(id.toLowerCase())} canEdit={canEdit} busy={busy} onFlag={onFlag} />
    </>
  );
  return (
    <div className="bg-[#151B14] border border-[#3A2E22] p-4 space-y-5" data-testid="mod-backup-diff">
      <p className="font-mono-ui text-[10px] text-[#5C6B51]">
        Compares what's in the boxes above (including unsaved edits) with the live Production lines. Matching is case-insensitive.
      </p>
      {FIELDS.map((f) => {
        const b = parseLine(drafts[f.key]);
        const p = parseLine(production[f.prod]);
        const bs = new Set(b.map((x) => x.toLowerCase()));
        const ps = new Set(p.map((x) => x.toLowerCase()));
        const onlyB = b.filter((x) => !ps.has(x.toLowerCase()));
        const onlyP = p.filter((x) => !bs.has(x.toLowerCase()));
        const shared = b.filter((x) => ps.has(x.toLowerCase()));
        return (
          <div key={f.key} data-testid={`mod-backup-diff-${f.key}`}>
            <div className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-[#E2E4DF] mb-2 flex items-center gap-2">
              {f.label}
              {f.flaggable && shared.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setShowShared((v) => !v)}
                  data-testid="mod-backup-shared-toggle"
                  className="text-[#8D9A87] hover:text-[#D9A066] transition-colors"
                >
                  · {shared.length} shared {showShared ? "▾ hide" : "▸ show"}
                </button>
              ) : (
                <span className="text-[#5C6B51]">· {shared.length} shared</span>
              )}
            </div>
            {f.flaggable && showShared && shared.length > 0 && (
              <div className="mb-3">
                <DiffList title={`In both (${shared.length})`} tone="neutral" items={shared} testid="mod-backup-shared-mods" renderExtra={modExtra} />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <DiffList
                title={`Only in backup (${onlyB.length})`}
                tone="amber"
                items={onlyB}
                testid={`mod-backup-only-backup-${f.key}`}
                renderExtra={f.flaggable ? modExtra : null}
              />
              <DiffList title={`Only in Production (${onlyP.length})`} tone="green" items={onlyP} testid={`mod-backup-only-prod-${f.key}`} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DiffList({ title, tone, items, testid, renderExtra }) {
  const color = {
    amber: "text-[#D9A066] border-[#5A4430]",
    green: "text-[#7FBF7F] border-[#2F4A2F]",
    neutral: "text-[#8D9A87] border-[#283524]",
  }[tone];
  return (
    <div className={`border ${color.split(" ")[1]} bg-[#0D110C]`} data-testid={testid}>
      <div className={`px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] ${color.split(" ")[0]} border-b border-[#1E271C]`}>{title}</div>
      <ul className="max-h-64 overflow-y-auto divide-y divide-[#1E271C]">
        {items.length === 0 && <li className="px-3 py-2 font-mono-ui text-[10px] text-[#5C6B51]">// none</li>}
        {items.map((id) => (
          <li key={id} className="px-3 py-1.5 flex items-center gap-2 font-mono-ui text-[11px] text-[#E2E4DF]">
            <span className="truncate flex-1" title={id}>{id}</span>
            {renderExtra && renderExtra(id)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FrameworkToggle({ id, on, canEdit, busy, onToggle }) {
  if (!on && !canEdit) return null;
  if (on) {
    return (
      <button
        type="button"
        disabled={!canEdit || busy}
        onClick={() => onToggle(id, false)}
        title={canEdit ? "Framework. Click to remove the tag" : "Framework"}
        data-testid={`mod-backup-framework-chip-${id}`}
        className="shrink-0 bg-[#5D8FBF]/20 text-[#8DB4D9] border border-[#5D8FBF]/50 hover:border-[#8DB4D9] px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] transition-colors"
      >
        Framework
      </button>
    );
  }
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => onToggle(id, true)}
      title="Tag as framework"
      data-testid={`mod-backup-framework-${id}`}
      className="shrink-0 text-[#8D9A87] hover:text-[#8DB4D9] transition-colors"
    >
      <Layers size={12} />
    </button>
  );
}

function FlagControl({ id, flag, canEdit, busy, onFlag }) {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  if (flag) {
    return (
      <span className="inline-flex items-center gap-1.5 shrink-0">
        <span title={flag.reason || "No reason given"} className="bg-[#C0392B]/20 text-[#E57368] border border-[#C0392B]/50 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em]" data-testid={`mod-backup-flag-chip-${id}`}>
          Not approved
        </span>
        {canEdit && (
          <button type="button" disabled={busy} onClick={() => onFlag(id, false)} title="Un-flag" className="text-[#8D9A87] hover:text-[#E2E4DF]" data-testid={`mod-backup-unflag-${id}`}>
            <RotateCcw size={11} />
          </button>
        )}
      </span>
    );
  }
  if (!canEdit) return null;
  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)} title="Mark as not approved for re-packing" className="shrink-0 text-[#8D9A87] hover:text-[#E57368] transition-colors" data-testid={`mod-backup-flag-${id}`}>
        <Ban size={12} />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 shrink-0">
      <input
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") onFlag(id, true, reason); if (e.key === "Escape") setEditing(false); }}
        placeholder="Reason (optional)"
        maxLength={300}
        data-testid={`mod-backup-flag-reason-${id}`}
        className="w-36 bg-[#151B14] border border-[#C0392B]/60 text-[#E2E4DF] px-1.5 py-0.5 text-[10px] focus:outline-none"
      />
      <button type="button" disabled={busy} onClick={() => onFlag(id, true, reason)} className="text-[#E57368]" data-testid={`mod-backup-flag-confirm-${id}`}><Check size={12} /></button>
      <button type="button" onClick={() => setEditing(false)} className="text-[#8D9A87]"><X size={12} /></button>
    </span>
  );
}

function NotApprovedList({ flags, canEdit, busy, onFlag }) {
  if (flags.size === 0) return null;
  return (
    <div className="bg-[#151B14] border border-[#C0392B]/40 p-4" data-testid="mod-backup-not-approved">
      <div className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-[#E57368] mb-1">
        // Not approved for re-packing ({flags.size})
      </div>
      <p className="font-mono-ui text-[10px] text-[#5C6B51] mb-3">Explains why these mods are kept in the backup but left out of the Production pack.</p>
      <ul className="divide-y divide-[#1E271C]">
        {[...flags.values()].map((f) => (
          <li key={f.mod_id} className="py-2 flex items-start gap-3 font-mono-ui text-[11px]">
            <Ban size={12} className="text-[#E57368] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[#E2E4DF] break-all">{f.mod_id}</div>
              <div className="text-[#8D9A87] text-[10px]">
                {f.reason ? `"${f.reason}"` : "No reason given"} · {f.by} · {fmt(f.at)}
              </div>
            </div>
            {canEdit && (
              <button type="button" disabled={busy} onClick={() => onFlag(f.mod_id, false)} className="text-[10px] uppercase tracking-[0.2em] text-[#8D9A87] hover:text-[#E2E4DF]" data-testid={`mod-backup-list-unflag-${f.mod_id}`}>
                Un-flag
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
